'use client';
import { useEffect, useState } from 'react';
import { useRouter } from 'next/navigation';
import { useAuthStore } from '@/store/auth.store';
import { api } from '@/lib/api';
import { ALL_LANGS, LANG_LABELS, type Lang } from '@/lib/i18n';
import { LANG_STORAGE_KEY, useT } from '@/lib/useT';
import { writeLangCookie } from '@/lib/i18n-context';
import { Globe, Check, ChevronDown } from 'lucide-react';

interface Props {
  /** Compact icon-only button on dark headers; expanded shows native name. */
  compact?: boolean;
  /** Override default colour (defaults inherit from --c-text). */
  className?: string;
}

/**
 * Universal language switcher used on both public-facing pages (homepage,
 * track page, marketplace) and inside the dashboard header. Persists the
 * choice in localStorage AND, if the user is logged in, syncs back to the
 * server so the choice survives across devices.
 */
export function LanguageSwitcher({ compact, className }: Props) {
  const t = useT();
  const router = useRouter();
  const { user, setAuth } = useAuthStore();
  const [open, setOpen] = useState(false);
  const [current, setCurrent] = useState<Lang>('DE');

  useEffect(() => {
    if (user?.language) {
      setCurrent(user.language as Lang);
      return;
    }
    try {
      const stored = localStorage.getItem(LANG_STORAGE_KEY);
      if (stored) {
        setCurrent(stored as Lang);
        return;
      }
    } catch {/* private mode */}
    // Best-effort browser guess
    if (typeof navigator !== 'undefined') {
      const code = navigator.language.slice(0, 2).toLowerCase();
      const map: Record<string, Lang> = {
        en: 'EN', de: 'DE', ru: 'RU', uk: 'UA',
        pl: 'PL', cs: 'CZ', ro: 'MD', bg: 'BG', kk: 'KZ',
      };
      if (map[code]) setCurrent(map[code]);
    }
  }, [user?.language]);

  const setLang = async (lang: Lang) => {
    setCurrent(lang);
    setOpen(false);
    // Cookie so the SSR layout renders this language on the next request
    // (no hydration flash); localStorage for cross-tab + legacy readers.
    writeLangCookie(lang);
    try {
      localStorage.setItem(LANG_STORAGE_KEY, lang);
      window.dispatchEvent(new Event('logixa-lang-change'));
    } catch {/* private mode */}
    // Re-render server components for the current route in the new language.
    // Client pages already reacted to logixa-lang-change; SSR pages (e.g. the
    // blog post, which reads the logixa_lang cookie at request time) need this
    // refresh to re-fetch their localized content. Cheap RSC-only refetch.
    try { router.refresh(); } catch {/* noop */}
    // If logged-in, persist server-side so other devices pick it up too.
    if (user) {
      try {
        await api.patch('/users/me', { language: lang });
        // Mirror into the auth store so useT picks it up immediately for
        // the rest of the session without a full reload.
        const tokens = useAuthStore.getState();
        if (tokens.token && tokens.refreshToken && tokens.user) {
          setAuth(tokens.token, tokens.refreshToken, { ...tokens.user, language: lang });
        }
      } catch {/* server may reject — UI choice still applies via localStorage */}
    }
  };

  const meta = LANG_LABELS[current] || LANG_LABELS.DE;

  return (
    <div className={`relative ${className || ''}`}>
      <button
        onClick={() => setOpen(!open)}
        className="inline-flex items-center gap-1.5 px-2.5 py-1.5 rounded-lg text-sm transition-colors hover:bg-white/10"
        title={t('langsw_choose')}
      >
        {compact ? (
          <>
            <span className="text-base leading-none">{meta.flag}</span>
            <span className="text-xs font-bold uppercase tracking-wider">{current}</span>
          </>
        ) : (
          <>
            <Globe size={14} />
            <span className="text-base leading-none">{meta.flag}</span>
            <span className="font-medium">{meta.native}</span>
            <ChevronDown size={12} />
          </>
        )}
      </button>

      {open && (
        <>
          {/* Click-outside catcher */}
          <div className="fixed inset-0 z-40" onClick={() => setOpen(false)} />
          <div
            className="absolute right-0 top-full mt-1 z-50 rounded-xl overflow-hidden shadow-2xl min-w-[180px]"
            style={{
              background: 'var(--c-card, #1a1a1a)',
              border: '1px solid var(--c-border, rgba(255,255,255,0.1))',
            }}
          >
            {ALL_LANGS.map(l => {
              const m = LANG_LABELS[l];
              const isCurrent = l === current;
              return (
                <button
                  key={l}
                  onClick={() => setLang(l)}
                  className="w-full px-3 py-2 text-left text-sm flex items-center gap-2 transition-colors hover:bg-white/5"
                  style={{ color: 'var(--c-text, #e5e5e5)' }}
                >
                  <span className="text-base leading-none">{m.flag}</span>
                  <span className="flex-1">{m.native}</span>
                  <span className="text-[10px] font-mono opacity-50">{l}</span>
                  {isCurrent && <Check size={12} style={{ color: 'var(--c-accent, #2563eb)' }} />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
